import { Link, useRouterState } from '@tanstack/react-router';
import {
  LayoutDashboard,
  PlayCircle,
  PlusCircle,
  Settings,
  Package,
  Users,
  FileText,
  Building2,
  GitBranch,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks';
import { hasPermission, type Permission } from '@/types';
import type { ReactNode } from 'react';

interface NavItem {
  path: string;
  label: string;
  icon: ReactNode;
  requiredPermission: Permission;
  exactMatch?: boolean | undefined;
}

const mainNavItems: NavItem[] = [
  {
    path: '/',
    label: 'Dashboard',
    icon: <LayoutDashboard className="h-5 w-5" />,
    requiredPermission: 'read',
    exactMatch: true,
  },
  {
    path: '/migrations',
    label: 'Migrations',
    icon: <PlayCircle className="h-5 w-5" />,
    requiredPermission: 'read',
    exactMatch: true,
  },
  // Write flow — the route is also <OperatorGuard>-wrapped in routes/migrations/new.tsx
  // (which dispatches to routes/migrations/mass.tsx, also guarded). The guard catches
  // direct-URL access; the requiredPermission here just hides the menu entry. Keep
  // Sidebar + Header + route guard aligned when changing the permission level.
  {
    path: '/migrations/new',
    label: 'New Migration',
    icon: <PlusCircle className="h-5 w-5" />,
    requiredPermission: 'write',
    exactMatch: true,
  },
];

const adminNavItems: NavItem[] = [
  {
    path: '/admin/settings',
    label: 'Settings',
    icon: <Settings className="h-5 w-5" />,
    requiredPermission: 'admin',
  },
  {
    path: '/admin/modules',
    label: 'Modules',
    icon: <Package className="h-5 w-5" />,
    requiredPermission: 'admin',
  },
  {
    path: '/admin/users',
    label: 'Users',
    icon: <Users className="h-5 w-5" />,
    requiredPermission: 'admin',
  },
  {
    path: '/admin/audit',
    label: 'Audit Log',
    icon: <FileText className="h-5 w-5" />,
    requiredPermission: 'admin',
  },
];

// Tools section — read-only diagnostics available to Viewers/Operators/Admins.
// IMPORTANT: when adjusting requiredPermission here, update the matching <ViewerGuard>
// / <OperatorGuard> on the destination route under src/routes/tools/ AND the matching
// entries in Header.tsx. The three must stay in lockstep; otherwise users see menu
// items they can't open (or vice versa).
const toolsNavItems: NavItem[] = [
  {
    path: '/tools/amplify-org',
    label: 'Amplify Org',
    icon: <Building2 className="h-5 w-5" />,
    requiredPermission: 'read',
  },
  {
    path: '/tools/bridge-analysis',
    label: 'Bridge Analysis',
    icon: <GitBranch className="h-5 w-5" />,
    requiredPermission: 'read',
  },
];

export function Sidebar() {
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;
  const { user, isLoadingRoles, meCheckFailed } = useAuth();
  const roles = user?.roles ?? [];
  // Authenticated but the backend has no ApplicationUsers row for them yet —
  // an admin needs to provision them at /admin/users. Without this hint the
  // sidebar would silently render empty, which is the symptom users report.
  //
  // `!meCheckFailed` keeps a transient 401/5xx/network failure (e.g. an idle-
  // expired access token) from masquerading as a provisioning gap — those go
  // through AuthGuard's verification-failed recovery page, not this hint.
  const showUnprovisionedHint =
    !isLoadingRoles && !meCheckFailed && user !== null && !user.isProvisioned;

  const visibleMainItems = mainNavItems.filter((item) =>
    roles.some((role) => hasPermission(role, item.requiredPermission))
  );

  const visibleToolsItems = toolsNavItems.filter((item) =>
    roles.some((role) => hasPermission(role, item.requiredPermission))
  );

  const visibleAdminItems = adminNavItems.filter((item) =>
    roles.some((role) => hasPermission(role, item.requiredPermission))
  );

  return (
    <aside className="hidden lg:flex fixed inset-y-0 left-0 z-40 w-64 flex-col border-r bg-background pt-14">
      <nav className="flex-1 space-y-1 p-4">
        {isLoadingRoles ? (
          <>
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-9 rounded-lg bg-muted animate-pulse mx-1"
              />
            ))}
          </>
        ) : (
          <>
            {showUnprovisionedHint && (
              <div className="rounded-lg border border-dashed border-muted-foreground/40 bg-muted/40 p-3 text-xs text-muted-foreground">
                Your account hasn&apos;t been provisioned for this app yet. Ask
                an administrator to add you at <span className="font-mono">Admin → Users</span>.
              </div>
            )}
            {visibleMainItems.map((item) => (
              <NavLink key={item.path} item={item} currentPath={currentPath} />
            ))}

            {visibleToolsItems.length > 0 && (
              <>
                <div className="pt-4 pb-2">
                  <span className="px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Tools
                  </span>
                </div>
                {visibleToolsItems.map((item) => (
                  <NavLink key={item.path} item={item} currentPath={currentPath} />
                ))}
              </>
            )}

            {visibleAdminItems.length > 0 && (
              <>
                <div className="pt-4 pb-2">
                  <span className="px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Admin
                  </span>
                </div>
                {visibleAdminItems.map((item) => (
                  <NavLink key={item.path} item={item} currentPath={currentPath} />
                ))}
              </>
            )}
          </>
        )}
      </nav>
    </aside>
  );
}

function NavLink({ item, currentPath }: { item: NavItem; currentPath: string }) {
  const isActive = item.exactMatch
    ? currentPath === item.path
    : currentPath === item.path || currentPath.startsWith(item.path + '/');

  return (
    <Link
      to={item.path}
      className={cn(
        'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
        isActive
          ? 'bg-primary text-primary-foreground'
          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
      )}
    >
      {item.icon}
      {item.label}
    </Link>
  );
}
